import type { ReactNode } from 'react';
import { INK, LOOP, ORANGE } from './app-impact-style';
import { Animate, AnimateTransform, useSmilTimeline } from '../smil';

/** Where a new coin leaves the phone. */
const PHONE_TOP = { x: 345, y: 872 };
/** One distance for all, so they keep formation; it clears the card's top margin before the coin fades. */
const RISE = -560;

type CoinShape = { cx: number; cy: number; disc: string; euro: readonly string[] };

/** Top to bottom; the lowest coin sits nearest the phone, so it leaves first and is replaced first. */
const COINS: readonly CoinShape[] = [
	{
		cx: 256,
		cy: 461,
		disc: 'M208.23 510.369C163.36 468.339 196.89 394.799 259.03 400.069C285.93 402.349 312.98 428.559 315.53 455.569C321.18 515.369 252.46 551.789 208.23 510.369Z',
		euro: [
			'M254.62 429.051C254.62 429.051 220.9 436.301 227 468.451C232.58 497.881 261.32 498.631 276.33 486.781',
			'M214.641 467.338L254.951 453.898',
			'M219 483.489L259.32 470.059',
		],
	},
	{
		cx: 177,
		cy: 610,
		disc: 'M138.91 650.029C101.5 614.989 129.46 553.669 181.27 558.059C203.7 559.959 226.25 581.809 228.38 604.339C233.09 654.199 175.79 684.569 138.92 650.029H138.91Z',
		euro: [
			'M179.22 580.691C179.22 580.691 149.79 587.011 155.11 615.081C159.98 640.761 185.06 641.421 198.16 631.081',
			'M144.34 614.099L179.51 602.379',
			'M148.141 628.201L183.321 616.48',
		],
	},
	{
		cx: 264,
		cy: 718,
		disc: 'M214.46 719.861C212.89 671.861 272.03 649.761 303.45 686.991C317.05 703.111 317.51 732.521 304 748.851C274.09 785.001 216.01 767.161 214.46 719.851V719.861Z',
		euro: [
			'M280.781 694.839C280.781 694.839 254.401 687.219 246.571 712.079C239.401 734.829 259.281 746.169 274.271 743.509',
			'M238.33 706.641L271.66 712.401',
			'M235.301 719.621L268.641 725.371',
		],
	},
];

/** Seconds: the tap lands at 1.58 and the lowest coin reacts first. */
const LEAVE_AT = [1.78, 1.7, 1.62];
/** After the last one has left, so the burst and the refill read as two beats. */
const ARRIVE_AT = [2.64, 2.42, 2.2];
const LEAVE_DRIFT = [18, -14, 10];
const LEAVE_SPIN = [28, -24, 32];
/** Back home while invisible, then shown again as the loop wraps and the arriving copy hides. */
const RESET_AT = LOOP - 0.1;

/** Centred on its own origin, so scale and rotate pivot on the coin. Bob phases differ per coin. */
function Coin(props: { shape: CoinShape; index: number; children: ReactNode }) {
	const motion = useSmilTimeline().enabled;
	const { cx, cy, disc, euro } = props.shape;
	return (
		<g transform={`translate(${cx} ${cy})`}>
			<g>
				{motion && (
					<animateTransform
						attributeName="transform"
						type="translate"
						values="0 0;0 -6;0 0"
						keyTimes="0;0.5;1"
						calcMode="spline"
						keySplines="0.42 0 0.58 1;0.42 0 0.58 1"
						dur={`${LOOP / 3}s`}
						begin={`${-0.7 * props.index}s`}
						repeatCount="indefinite"
					/>
				)}
				{props.children}
				<g transform={`translate(${-cx} ${-cy})`}>
					<path d={disc} fill={ORANGE} />
					{euro.map((d) => (
						<path key={d} d={d} stroke={INK} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
					))}
				</g>
			</g>
		</g>
	);
}

function LeavingCoin(props: { shape: CoinShape; index: number }) {
	const at = LEAVE_AT[props.index] ?? 0;
	const drift = LEAVE_DRIFT[props.index] ?? 0;
	return (
		<g>
			<Animate
				attr="opacity"
				keys={[
					[at + 0.7, 1],
					[at + 0.75, 0],
					[LOOP - 0.05, 0],
					[LOOP, 1],
				]}
			/>
			<AnimateTransform
				type="translate"
				keys={[
					[at, '0 0'],
					[at + 0.12, `${-drift * 0.2} 10`, 'out'],
					[at + 0.75, `${drift} ${RISE}`, 'launch'],
					[RESET_AT, `${drift} ${RISE}`],
					[RESET_AT + 0.02, '0 0'],
				]}
			/>
			<Coin shape={props.shape} index={props.index}>
				<AnimateTransform
					type="rotate"
					keys={[
						[at + 0.12, 0],
						[at + 0.75, LEAVE_SPIN[props.index] ?? 0],
						[RESET_AT, LEAVE_SPIN[props.index] ?? 0],
						[RESET_AT + 0.02, 0],
					]}
				/>
			</Coin>
		</g>
	);
}

/** Hidden in the static drawing: it only exists to refill the spot a leaving coin emptied. */
function ArrivingCoin(props: { shape: CoinShape; index: number }) {
	const at = ARRIVE_AT[props.index] ?? 0;
	const sx = PHONE_TOP.x - props.shape.cx;
	const sy = PHONE_TOP.y - props.shape.cy;
	return (
		<g opacity={0}>
			<Animate
				attr="opacity"
				keys={[
					[at - 0.01, 0],
					[at, 1],
				]}
			/>
			<AnimateTransform
				type="translate"
				keys={[
					[at, `${sx} ${sy}`],
					[at + 0.5, `${sx * 0.65} ${sy * 0.3}`, 'out'],
					[at + 0.95, '0 -8'],
					[at + 1.2, '0 0'],
				]}
			/>
			<Coin shape={props.shape} index={props.index}>
				<AnimateTransform
					type="scale"
					keys={[
						[at, 0.2],
						[at + 0.25, 1.12, 'out'],
						[at + 0.45, 1],
					]}
				/>
				<AnimateTransform
					type="rotate"
					additive
					keys={[
						[at, -20],
						[at + 0.95, 6],
						[at + 1.2, 0],
					]}
				/>
			</Coin>
		</g>
	);
}

export function Coins() {
	return COINS.map((shape, index) => (
		<g key={shape.cx}>
			<LeavingCoin shape={shape} index={index} />
			<ArrivingCoin shape={shape} index={index} />
		</g>
	));
}
